import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './SearchBar.css';

/**
 * SearchBar Component - Global search
 *
 * @param {string} placeholder - Placeholder text
 * @param {function} onSearch - Search handler
 * @param {boolean} autoFocus - Auto focus on mount
 */
function SearchBar({
  placeholder = 'Buscar incidencia, comunidad, vecino...',
  onSearch,
  autoFocus = false
}) {
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Keyboard shortcut: Ctrl+K or Cmd+K
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      // Escape to clear
      if (e.key === 'Escape') {
        setQuery('');
        inputRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      // Navigate to incidencias with search query
      navigate(`/incidencias?search=${encodeURIComponent(query)}`);
      if (onSearch) onSearch(query);
    }
  };

  const handleClear = () => {
    setQuery('');
    inputRef.current?.focus();
  };

  return (
    <form
      className={`ui-searchbar ${isFocused ? 'ui-searchbar--focused' : ''}`}
      onSubmit={handleSearch}
    >
      <span className="ui-searchbar__icon">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
      </span>

      <input
        ref={inputRef}
        type="text"
        className="ui-searchbar__input"
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        autoFocus={autoFocus}
      />

      {query ? (
        <button
          type="button"
          className="ui-searchbar__clear"
          onClick={handleClear}
          aria-label="Clear search"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      ) : (
        <span className="ui-searchbar__shortcut">
          <kbd>Ctrl K</kbd>
        </span>
      )}
    </form>
  );
}

export default SearchBar;
